import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { getRecipeImage } from "../../utils/recipeImages";
import { RecipesGridSkeleton } from "../../components/ui/skeleton-loaders";
import ModuleGate from "../../components/ModuleGate";
import { 
  ArrowLeft, 
  Clock, 
  ChefHat, 
  Flame,
  Filter,
  X,
  Heart,
  History,
  Sparkles
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ INGREDIENT FORMATTING HELPERS ============
// Foods that should NEVER be converted to units (always show in grams/ml)
const EXCLUDED_FROM_UNITS = ['clara', 'claras', 'yema', 'yemas'];

// Foods that should be displayed in units (not grams)
const UNIT_FOODS = {
  'huevo entero': { unit: 'unidad', gramsPerUnit: 50, singular: 'huevo', plural: 'huevos' },
  'huevos enteros': { unit: 'unidad', gramsPerUnit: 50, singular: 'huevo', plural: 'huevos' },
  'huevo duro': { unit: 'unidad', gramsPerUnit: 50, singular: 'huevo', plural: 'huevos' },
  'tortita': { unit: 'unidad', gramsPerUnit: 40, singular: 'tortita', plural: 'tortitas' },
  'wrap': { unit: 'unidad', gramsPerUnit: 60, singular: 'wrap', plural: 'wraps' },
  'tortilla de trigo': { unit: 'unidad', gramsPerUnit: 60, singular: 'tortilla', plural: 'tortillas' },
  'yogur': { unit: 'unidad', gramsPerUnit: 125, singular: 'yogur', plural: 'yogures' },
  'atún en lata': { unit: 'lata', gramsPerUnit: 60, singular: 'lata', plural: 'latas' },
  'pan de hamburguesa': { unit: 'unidad', gramsPerUnit: 50, singular: 'pan', plural: 'panes' },
  'pan de molde': { unit: 'rebanada', gramsPerUnit: 30, singular: 'rebanada', plural: 'rebanadas' },
  'pan integral': { unit: 'rebanada', gramsPerUnit: 30, singular: 'rebanada', plural: 'rebanadas' },
  'manzana': { unit: 'unidad', gramsPerUnit: 180, singular: 'manzana', plural: 'manzanas' },
  'plátano': { unit: 'unidad', gramsPerUnit: 120, singular: 'plátano', plural: 'plátanos' },
  'naranja': { unit: 'unidad', gramsPerUnit: 200, singular: 'naranja', plural: 'naranjas' },
  'kiwi': { unit: 'unidad', gramsPerUnit: 80, singular: 'kiwi', plural: 'kiwis' },
};

// Minimum quantities for key ingredients (to avoid absurd amounts)
const MIN_QUANTITIES = {
  'atún': 60, 'atún en lata': 60, 'atún al natural': 60,
  'salmón': 80, 'pechuga de pollo': 80, 'pollo': 80,
  'ternera': 80, 'pavo': 60, 'merluza': 100,
  'pasta': 60, 'arroz': 50, 'quinoa': 40,
  'patata': 100, 'boniato': 100,
  'huevo entero': 50, 'huevo': 50,
  'claras de huevo': 60, 'claras': 60,
  'aceite': 5, 'aceite de oliva': 5,
};

/**
 * Format ingredient for display with smart units
 * RULES:
 * - Claras, yemas → ALWAYS grams/ml (never units)
 * - Huevos enteros → units (2 huevos)
 * - Atún en lata → latas
 * - Frutas → units when whole
 */
const formatIngredient = (ing) => {
  // Handle different data structures
  const foodName = ing.food_name || ing.nombre || ing.name || '';
  const quantity = ing.cantidad || ing.quantity || 0;
  const unit = ing.unidad || ing.unit || 'g';
  
  if (!foodName) return null;
  
  const foodLower = foodName.toLowerCase();
  
  // FIRST: Check if this food is EXCLUDED from unit conversion (claras, yemas)
  const isExcluded = EXCLUDED_FROM_UNITS.some(exc => foodLower.includes(exc));
  
  // Only convert to units if NOT excluded
  if (!isExcluded) {
    // Check if this food should use units instead of grams
    const unitFood = Object.entries(UNIT_FOODS).find(([key]) => foodLower.includes(key));
    
    if (unitFood && (unit === 'g' || unit === 'gr' || !unit)) {
      const [, config] = unitFood;
      const numUnits = Math.round(quantity / config.gramsPerUnit);
      
      if (numUnits > 0) {
        const displayUnit = numUnits === 1 ? config.singular : config.plural;
        return {
          name: foodName,
          display: `${numUnits} ${displayUnit}`,
          quantity: numUnits,
          unit: displayUnit
        };
      }
    }
  }
  
  // Apply minimum quantities for key ingredients
  let finalQuantity = quantity;
  const minEntry = Object.entries(MIN_QUANTITIES).find(([key]) => foodLower.includes(key));
  if (minEntry && quantity < minEntry[1] && quantity > 0) {
    finalQuantity = minEntry[1];
  }
  
  // Standard formatting (grams/ml)
  const displayUnit = unit === 'gr' ? 'g' : unit;
  
  return {
    name: foodName,
    display: `${Math.round(finalQuantity)}${displayUnit}`,
    quantity: finalQuantity,
    unit: displayUnit
  };
};

// Filtros disponibles
const OBJETIVOS = [
  { id: "todos", label: "Todos", color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/10" },
  { id: "definicion", label: "Definición", color: "text-blue-400", bg: "bg-blue-500/10" },
  { id: "musculacion", label: "Musculación", color: "text-red-400", bg: "bg-red-500/10" },
  { id: "recomposicion", label: "Recomposición", color: "text-purple-400", bg: "bg-purple-500/10" },
];

const CATEGORIAS = [
  { id: "todos", label: "Todas", icon: "🍽️" },
  { id: "desayuno", label: "Desayunos", icon: "🌅" },
  { id: "merienda", label: "Almuerzo / Merienda", icon: "🥪" },
  { id: "comida", label: "Comidas", icon: "🍲" },
  { id: "cena", label: "Cenas", icon: "🌙" },
  { id: "snack", label: "Snacks", icon: "🥜" },
  { id: "postre", label: "Postres", icon: "🍨" },
  { id: "post-entreno", label: "Post-entreno", icon: "💪" },
];

const DificultadBadge = ({ dificultad }) => {
  const colors = {
    "muy fácil": "bg-green-500/20 text-green-400",
    "fácil": "bg-emerald-500/20 text-emerald-400",
    "media": "bg-yellow-500/20 text-yellow-400",
    "difícil": "bg-red-500/20 text-red-400",
  };
  
  return (
    <span className={cn("px-2 py-0.5 rounded text-xs", colors[dificultad] || "bg-gray-500/20 text-gray-400")}>
      {dificultad}
    </span>
  );
};

// Using shared getRecipeImage from utils/recipeImages.js

const RecipeCard = ({ recipe, onClick, isFavorite, onToggleFavorite }) => {
  const objetivo = OBJETIVOS.find(o => o.id === recipe.objetivo);
  // Extract time as number from "10 min" format
  const tiempoStr = recipe.tiempo_preparacion || recipe.tiempo_minutos || '';
  const tiempo = parseInt(tiempoStr) || tiempoStr;
  
  // Get first category for display
  const categoria = Array.isArray(recipe.categoria) ? recipe.categoria[0] : recipe.categoria;
  const categoriaConfig = CATEGORIAS.find(c => c.id === categoria);
  
  // Get macros (handle both naming conventions)
  const macros = recipe.macros || {};
  const calorias = macros.calorias || macros.kcal || 0;
  const proteina = macros.proteina || macros.proteinas || 0;
  const carbos = macros.carbos || macros.carbohidratos || 0;
  const grasas = macros.grasas || 0;
  const hasMacros = calorias > 0;
  
  const imageUrl = getRecipeImage(recipe);
  
  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    onToggleFavorite?.(recipe.id);
  };
  
  return (
    <div 
      onClick={onClick}
      className="bg-[#1A1A1A] rounded-xl overflow-hidden cursor-pointer hover:bg-[#222] transition-all border border-[#2A2A2A] hover:border-[#D4AF37]/30 hover:shadow-lg hover:shadow-[#D4AF37]/5"
      data-testid={`recipe-card-${recipe.id}`}
    >
      {/* Image */}
      <div className="relative h-32 bg-[#0B0B0B]">
        <img 
          src={imageUrl} 
          alt={recipe.nombre}
          className="w-full h-full object-cover opacity-80"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent" />
        
        {/* Favorite button */}
        <button
          onClick={handleFavoriteClick}
          className={cn(
            "absolute top-2 right-2 p-2 rounded-full backdrop-blur-sm transition-all",
            isFavorite 
              ? "bg-[#D4AF37]/20 text-[#D4AF37]" 
              : "bg-black/50 text-white/60 hover:text-[#D4AF37] hover:bg-black/70"
          )}
          data-testid={`favorite-btn-${recipe.id}`}
        >
          <Heart className={cn("w-4 h-4", isFavorite && "fill-current")} />
        </button>
        
        {/* Badges on image */}
        <div className="absolute top-2 left-2 flex gap-1.5">
          {categoriaConfig && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-black/60 backdrop-blur-sm text-white flex items-center gap-1">
              <span>{categoriaConfig.icon}</span> {categoriaConfig.label}
            </span>
          )}
        </div>
        
        {objetivo && objetivo.id !== 'todos' && (
          <span className={cn(
            "absolute bottom-12 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold backdrop-blur-sm",
            objetivo.bg, objetivo.color
          )}>
            {objetivo.label}
          </span>
        )}
        
        {/* Macros overlay */}
        {hasMacros && (
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
            <div className="flex items-center gap-1 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-full">
              <Flame className="w-3 h-3 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-[10px] font-bold">{calorias} kcal</span>
            </div>
            <div className="flex items-center gap-2 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-full">
              <span className="text-red-400 text-[10px] font-medium">{proteina}P</span>
              <span className="text-yellow-400 text-[10px] font-medium">{carbos}C</span>
              <span className="text-blue-400 text-[10px] font-medium">{grasas}G</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-3">
        <h3 className="text-white font-semibold text-sm leading-tight mb-1">{recipe.nombre}</h3>
        
        {/* Tips as description */}
        {recipe.tips_goatgym && (
          <p className="text-[#666] text-xs mb-2 line-clamp-1">{recipe.tips_goatgym}</p>
        )}
        
        {/* Meta info */}
        <div className="flex items-center gap-3 text-xs text-[#888]">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{tiempo} min</span>
          </div>
          <DificultadBadge dificultad={recipe.dificultad} />
        </div>
      </div>
    </div>
  );
};

const RecipeDetail = ({ recipe, onClose }) => {
  const objetivo = OBJETIVOS.find(o => o.id === recipe.objetivo);
  // Extract time
  const tiempoStr = recipe.tiempo_preparacion || recipe.tiempo_minutos || '';
  const tiempo = parseInt(tiempoStr) || tiempoStr;
  
  // Get category
  const categoria = Array.isArray(recipe.categoria) ? recipe.categoria[0] : recipe.categoria;
  const categoriaDisplay = CATEGORIAS.find(c => c.id === categoria)?.label || categoria;
  
  // Get macros
  const macros = recipe.macros || {};
  const calorias = macros.calorias || macros.kcal || 0;
  const proteina = macros.proteina || macros.proteinas || 0;
  const carbos = macros.carbos || macros.carbohidratos || 0;
  const grasas = macros.grasas || 0;
  const hasMacros = calorias > 0;
  
  return (
    <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto">
      <div className="min-h-screen pb-20">
        {/* Header */}
        <div className="sticky top-0 bg-[#0B0B0B]/95 backdrop-blur-sm border-b border-[#2A2A2A] p-4 z-10">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5" />
            </button>
            <h1 className="text-white font-bold text-lg flex-1">{recipe.nombre}</h1>
          </div>
        </div>
        
        <div className="p-4 space-y-6">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-2">
            {categoriaDisplay && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#D4AF37]/20 text-[#D4AF37]">
                {categoriaDisplay}
              </span>
            )}
            {objetivo && objetivo.id !== 'todos' && (
              <span className={cn("px-3 py-1 rounded-full text-xs font-medium", objetivo.bg, objetivo.color)}>
                {objetivo.label}
              </span>
            )}
            <div className="flex items-center gap-1 px-3 py-1 bg-[#1A1A1A] rounded-full text-[#888] text-xs">
              <Clock className="w-3 h-3" />
              <span>{tiempo}</span>
            </div>
            <DificultadBadge dificultad={recipe.dificultad} />
          </div>
          
          {/* Macros Card - only show if calculated */}
          {hasMacros && (
            <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 rounded-xl p-4 border border-[#D4AF37]/20">
              <h3 className="text-[#D4AF37] font-semibold text-sm mb-3">Macros aproximados</h3>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div>
                  <div className="text-[#D4AF37] font-bold text-lg">{calorias}</div>
                  <div className="text-[#888] text-xs">kcal</div>
                </div>
                <div>
                  <div className="text-red-400 font-bold text-lg">{proteina}g</div>
                  <div className="text-[#888] text-xs">Proteína</div>
                </div>
                <div>
                  <div className="text-yellow-400 font-bold text-lg">{carbos}g</div>
                  <div className="text-[#888] text-xs">Carbos</div>
                </div>
                <div>
                  <div className="text-blue-400 font-bold text-lg">{grasas}g</div>
                  <div className="text-[#888] text-xs">Grasas</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Ingredientes */}
          <div className="bg-[#1A1A1A] rounded-xl p-4 border border-[#2A2A2A]">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="text-lg">🛒</span> Ingredientes
            </h3>
            <ul className="space-y-2">
              {recipe.ingredientes?.map((ing, idx) => {
                const formatted = formatIngredient(ing);
                if (!formatted) return null;
                
                return (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="text-[#D4AF37] mt-0.5">•</span>
                    <span className="text-white font-medium">{formatted.name}:</span>
                    <span className="text-[#AAA]">{formatted.display}</span>
                    {ing.nota && <span className="text-[#666]">({ing.nota})</span>}
                  </li>
                );
              })}
            </ul>
          </div>
          
          {/* Preparación */}
          <div className="bg-[#1A1A1A] rounded-xl p-4 border border-[#2A2A2A]">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-[#D4AF37]" /> Preparación
            </h3>
            <ol className="space-y-3">
              {recipe.preparacion?.map((paso, idx) => (
                <li key={idx} className="flex gap-3 text-sm">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-[#AAA] pt-0.5">{paso}</span>
                </li>
              ))}
            </ol>
          </div>
          
          {/* Tips */}
          {(recipe.tips_goatgym || recipe.tips) && (
            <div className="bg-[#D4AF37]/10 rounded-xl p-4 border border-[#D4AF37]/20">
              <h3 className="text-[#D4AF37] font-semibold text-sm mb-2">💡 Tip GOATGYM</h3>
              <p className="text-[#AAA] text-sm">{recipe.tips_goatgym || recipe.tips}</p>
            </div>
          )}
          
          {/* Categorías (multiple) */}
          {Array.isArray(recipe.categoria) && recipe.categoria.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {recipe.categoria.map(cat => (
                <span key={cat} className="px-2 py-1 bg-[#2A2A2A] text-[#888] text-xs rounded">
                  #{cat}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ PREMIUM UPGRADE SCREEN ============
const PremiumUpgradeScreen = ({ onBack }) => {
  const benefits = [
    { icon: "🍽️", title: "Recetas Premium", desc: "100+ recetas exclusivas diseñadas para tus objetivos" },
    { icon: "🎯", title: "Nutrición Personalizada", desc: "Planes adaptados a definición, recomposición o musculación" },
    { icon: "📊", title: "Macros Calculados", desc: "Cada receta con proteínas, carbos y grasas exactos" },
    { icon: "🥗", title: "Selector Flexible", desc: "Elige tus comidas del día según tu objetivo calórico" },
    { icon: "⚡", title: "Snacks & Postres Fit", desc: "Opciones dulces sin salirte de tu dieta" },
    { icon: "🤖", title: "IA Nutricional", desc: "Próximamente: recomendaciones inteligentes" },
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0B] pb-24" data-testid="premium-upgrade-screen">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0B]/95 backdrop-blur-sm border-b border-[#2A2A2A] p-4 z-10">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-bold text-lg">Nutrición GOATGYM</h1>
            <p className="text-[#D4AF37] text-xs font-medium">Exclusivo Premium</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Hero Section */}
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#D4AF37]/20 to-[#0B0B0B] border border-[#D4AF37]/30">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800')] bg-cover bg-center opacity-20" />
          <div className="relative p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <span className="text-3xl">🥇</span>
            </div>
            <h2 className="text-white font-black text-2xl mb-2">
              NUTRICIÓN<br/><span className="text-[#D4AF37]">PREMIUM</span>
            </h2>
            <p className="text-[#888] text-sm max-w-xs mx-auto">
              Desbloquea el módulo completo de nutrición y lleva tu transformación al siguiente nivel
            </p>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="space-y-3">
          <h3 className="text-white font-semibold text-sm">¿Qué incluye Premium?</h3>
          <div className="grid gap-3">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx}
                className="bg-[#1A1A1A] rounded-xl p-4 border border-[#2A2A2A] flex items-start gap-3"
              >
                <span className="text-2xl">{benefit.icon}</span>
                <div>
                  <h4 className="text-white font-medium text-sm">{benefit.title}</h4>
                  <p className="text-[#666] text-xs mt-0.5">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <div className="bg-gradient-to-r from-[#1A1A1A] to-[#0B0B0B] rounded-xl p-4 border border-[#2A2A2A]">
          <p className="text-[#888] text-sm italic mb-2">
            "Con la nutrición premium perdí 8kg en 3 meses sin pasar hambre. Las recetas son increíbles."
          </p>
          <p className="text-[#D4AF37] text-xs font-medium">— Cliente GOATGYM Pro</p>
        </div>

        {/* CTA Buttons */}
        <div className="space-y-3 pt-4">
          <a
            href="https://wa.me/34600000000?text=Hola%2C%20quiero%20información%20sobre%20GOATGYM%20Premium"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-4 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold rounded-xl transition-all"
            data-testid="premium-whatsapp-cta"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            HABLAR CON MI COACH
          </a>
          
          <p className="text-center text-[#666] text-xs">
            Consulta sin compromiso • Respuesta en menos de 24h
          </p>
        </div>
      </div>
    </div>
  );
};

const RecetasPage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ objetivo: null, categoria: null, tiempo: null, dificultad: null });
  
  // NEW: Tabs and favorites state
  const [activeTab, setActiveTab] = useState('todas'); // 'todas', 'favoritos', 'recientes'
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [favoriteRecipes, setFavoriteRecipes] = useState([]);
  const [recentRecipes, setRecentRecipes] = useState([]);
  
  // Check if user is premium
  const isPremium = user?.is_premium === true;
  
  // Filters for tiempo and dificultad (moved up to avoid conditional rendering issues)
  const TIEMPOS = [
    { id: 'rapido', label: '⚡ Rápido (<15 min)' },
    { id: 'medio', label: '⏱️ Medio (>15 min)' },
  ];
  
  const DIFICULTADES = [
    { id: 'facil', label: '🟢 Fácil' },
    { id: 'media', label: '🟡 Media/Difícil' },
  ];
  
  // Fetch favorite IDs on mount
  const fetchFavoriteIds = useCallback(async () => {
    if (!isPremium) return;
    try {
      const response = await axios.get(`${API_URL}/api/premium/recipes/favorites/ids`, { headers: getAuthHeaders() });
      setFavoriteIds(new Set(response.data.ids || []));
    } catch (error) {
      console.error("Error fetching favorite IDs:", error);
    }
  }, [isPremium, getAuthHeaders]);
  
  // Fetch favorites list
  const fetchFavorites = useCallback(async () => {
    if (!isPremium) return;
    try {
      const response = await axios.get(`${API_URL}/api/premium/recipes/favorites`, { headers: getAuthHeaders() });
      setFavoriteRecipes(response.data.recipes || []);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  }, [isPremium, getAuthHeaders]);
  
  // Fetch recents list
  const fetchRecents = useCallback(async () => {
    if (!isPremium) return;
    try {
      const response = await axios.get(`${API_URL}/api/premium/recipes/recents`, { headers: getAuthHeaders() });
      setRecentRecipes(response.data.recipes || []);
    } catch (error) {
      console.error("Error fetching recents:", error);
    }
  }, [isPremium, getAuthHeaders]);
  
  const fetchRecipes = useCallback(async () => {
    if (!isPremium) return;
    
    try {
      const params = new URLSearchParams();
      if (filters.objetivo && filters.objetivo !== 'todos') params.append("objetivo", filters.objetivo);
      if (filters.categoria && filters.categoria !== 'todos') params.append("categoria", filters.categoria);
      
      const url = `${API_URL}/api/premium/recipes${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });
      
      let filteredRecipes = response.data.recipes || [];
      
      // Client-side filtering for tiempo and dificultad
      if (filters.tiempo) {
        filteredRecipes = filteredRecipes.filter(r => {
          const tiempo = parseInt(r.tiempo_preparacion) || 0;
          return filters.tiempo === 'rapido' ? tiempo <= 15 : tiempo > 15;
        });
      }
      
      if (filters.dificultad) {
        filteredRecipes = filteredRecipes.filter(r => {
          const dif = (r.dificultad || '').toLowerCase();
          if (filters.dificultad === 'facil') return dif.includes('fácil') || dif.includes('facil') || dif.includes('muy');
          return dif.includes('media') || dif.includes('difícil');
        });
      }
      
      setRecipes(filteredRecipes);
    } catch (error) {
      console.error("Error fetching recipes:", error);
    } finally {
      setLoading(false);
    }
  }, [filters, isPremium, getAuthHeaders]);
  
  // Toggle favorite
  const handleToggleFavorite = async (recipeId) => {
    try {
      await axios.post(`${API_URL}/api/premium/recipes/${recipeId}/favorite`, {}, { headers: getAuthHeaders() });
      
      // Update local state
      setFavoriteIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(recipeId)) {
          newSet.delete(recipeId);
        } else {
          newSet.add(recipeId);
        }
        return newSet;
      });
      
      // Refresh favorites if on that tab
      if (activeTab === 'favoritos') {
        fetchFavorites();
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };
  
  // Record view when opening recipe detail
  const handleRecipeClick = async (recipe) => {
    setSelectedRecipe(recipe);
    
    // Record the view
    try {
      await axios.post(`${API_URL}/api/premium/recipes/${recipe.id}/view`, {}, { headers: getAuthHeaders() });
    } catch (error) {
      // Silent fail for view recording
    }
  };
  
  useEffect(() => {
    if (isPremium) {
      fetchFavoriteIds();
      fetchRecipes();
    }
  }, [filters, isPremium, fetchFavoriteIds, fetchRecipes]);
  
  // Fetch tab-specific data when tab changes
  useEffect(() => {
    if (isPremium && activeTab === 'favoritos') {
      fetchFavorites();
    } else if (isPremium && activeTab === 'recientes') {
      fetchRecents();
    }
  }, [activeTab, isPremium, fetchFavorites, fetchRecents]);
  
  const clearFilters = () => {
    setFilters({ objetivo: null, categoria: null, tiempo: null, dificultad: null });
  };
  
  const activeFiltersCount = 
    (filters.objetivo && filters.objetivo !== 'todos' ? 1 : 0) + 
    (filters.categoria && filters.categoria !== 'todos' ? 1 : 0) +
    (filters.tiempo ? 1 : 0) +
    (filters.dificultad ? 1 : 0);
  
  // Get the recipes to display based on active tab
  const getDisplayRecipes = () => {
    switch (activeTab) {
      case 'favoritos':
        return favoriteRecipes;
      case 'recientes':
        return recentRecipes;
      default:
        return recipes;
    }
  };
  
  const displayRecipes = getDisplayRecipes();
  
  // If not premium, show upgrade screen
  if (!isPremium) {
    return <PremiumUpgradeScreen onBack={() => navigate(-1)} />;
  }
  
  // Tab config
  const TABS = [
    { id: 'todas', label: 'Todas', icon: Sparkles, count: recipes.length },
    { id: 'favoritos', label: 'Favoritos', icon: Heart, count: favoriteIds.size },
    { id: 'recientes', label: 'Recientes', icon: History, count: recentRecipes.length },
  ];
  
  return (
    <ModuleGate moduleId="recetas">
    <div className="min-h-screen bg-[#0B0B0B] pb-24" data-testid="recetas-page">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0B]/95 backdrop-blur-sm border-b border-[#2A2A2A] p-4 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-bold text-lg">Recetas Premium</h1>
            <p className="text-[#D4AF37] text-xs font-medium">🥇 {recipes.length} recetas exclusivas</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              "border-[#2A2A2A] text-white",
              activeFiltersCount > 0 && "border-[#D4AF37] text-[#D4AF37]"
            )}
          >
            <Filter className="w-4 h-4 mr-1" />
            {activeFiltersCount > 0 ? activeFiltersCount : "Filtros"}
          </Button>
        </div>
        
        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-[#2A2A2A] space-y-4">
            {/* Objetivo */}
            <div>
              <label className="text-[#888] text-xs mb-2 block">Objetivo</label>
              <div className="flex flex-wrap gap-2">
                {OBJETIVOS.map(obj => (
                  <button
                    key={obj.id}
                    onClick={() => setFilters(f => ({ ...f, objetivo: f.objetivo === obj.id ? null : obj.id }))}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                      filters.objetivo === obj.id 
                        ? `${obj.bg} ${obj.color} ring-1 ring-current` 
                        : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
                    )}
                  >
                    {obj.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Categoría */}
            <div>
              <label className="text-[#888] text-xs mb-2 block">Categoría</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIAS.filter(c => c.id !== 'todos').map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setFilters(f => ({ ...f, categoria: f.categoria === cat.id ? null : cat.id }))}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1",
                      filters.categoria === cat.id 
                        ? "bg-[#D4AF37]/20 text-[#D4AF37] ring-1 ring-[#D4AF37]" 
                        : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
                    )}
                  >
                    <span>{cat.icon}</span> {cat.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Tiempo */}
            <div>
              <label className="text-[#888] text-xs mb-2 block">Tiempo de preparación</label>
              <div className="flex flex-wrap gap-2">
                {TIEMPOS.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setFilters(f => ({ ...f, tiempo: f.tiempo === t.id ? null : t.id }))}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                      filters.tiempo === t.id 
                        ? "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-400" 
                        : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Dificultad */}
            <div>
              <label className="text-[#888] text-xs mb-2 block">Dificultad</label>
              <div className="flex flex-wrap gap-2">
                {DIFICULTADES.map(d => (
                  <button
                    key={d.id}
                    onClick={() => setFilters(f => ({ ...f, dificultad: f.dificultad === d.id ? null : d.id }))}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                      filters.dificultad === d.id 
                        ? "bg-orange-500/20 text-orange-400 ring-1 ring-orange-400" 
                        : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
                    )}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            
            {activeFiltersCount > 0 && (
              <button 
                onClick={clearFilters}
                className="text-[#D4AF37] text-xs underline"
              >
                Limpiar filtros ({activeFiltersCount})
              </button>
            )}
          </div>
        )}
        
        {/* TABS: Todas / Favoritos / Recientes */}
        <div className="mt-4 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all",
                  isActive 
                    ? "bg-[#D4AF37] text-black" 
                    : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
                )}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className={cn("w-3.5 h-3.5", isActive && tab.id === 'favoritos' && "fill-current")} />
                {tab.label}
                {tab.count > 0 && (
                  <span className={cn(
                    "ml-1 px-1.5 py-0.5 rounded-full text-[10px]",
                    isActive ? "bg-black/20" : "bg-[#2A2A2A]"
                  )}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        {loading && activeTab === 'todas' ? (
          <RecipesGridSkeleton />
        ) : displayRecipes.length === 0 ? (
          <div className="text-center py-20">
            {activeTab === 'favoritos' ? (
              <>
                <Heart className="w-12 h-12 text-[#444] mx-auto mb-4" />
                <p className="text-[#666]">No tienes recetas favoritas</p>
                <p className="text-[#555] text-sm mt-1">Toca el ❤️ en cualquier receta para guardarla</p>
              </>
            ) : activeTab === 'recientes' ? (
              <>
                <History className="w-12 h-12 text-[#444] mx-auto mb-4" />
                <p className="text-[#666]">No has visto recetas recientemente</p>
                <p className="text-[#555] text-sm mt-1">Explora la pestaña "Todas" para descubrir recetas</p>
              </>
            ) : (
              <>
                <ChefHat className="w-12 h-12 text-[#444] mx-auto mb-4" />
                <p className="text-[#666]">No hay recetas con estos filtros</p>
                {activeFiltersCount > 0 && (
                  <button onClick={clearFilters} className="text-[#D4AF37] text-sm mt-2 underline">
                    Limpiar filtros
                  </button>
                )}
              </>
            )}
          </div>
        ) : activeTab !== 'todas' || filters.categoria || filters.objetivo ? (
          /* Filtered/Tab view - simple grid */
          <div className="grid gap-4">
            {displayRecipes.map(recipe => (
              <RecipeCard 
                key={recipe.id} 
                recipe={recipe} 
                onClick={() => handleRecipeClick(recipe)}
                isFavorite={favoriteIds.has(recipe.id)}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </div>
        ) : (
          /* Unfiltered view - grouped by category */
          <div className="space-y-8">
            {CATEGORIAS.filter(cat => cat.id !== 'todos').map(categoria => {
              const categoryRecipes = displayRecipes.filter(r => {
                const recipeCats = Array.isArray(r.categoria) ? r.categoria : [r.categoria];
                return recipeCats.some(c => c?.toLowerCase() === categoria.id);
              });
              
              if (categoryRecipes.length === 0) return null;
              
              return (
                <div key={categoria.id} className="space-y-3">
                  {/* Category Header */}
                  <div className="flex items-center gap-2 sticky top-[140px] bg-[#0B0B0B] py-2 z-5">
                    <span className="text-lg">{categoria.icon}</span>
                    <h2 className="text-white font-bold text-base">{categoria.label}</h2>
                    <span className="text-[#666] text-xs">({categoryRecipes.length})</span>
                  </div>
                  
                  {/* Category Recipes */}
                  <div className="grid gap-3">
                    {categoryRecipes.slice(0, 6).map(recipe => (
                      <RecipeCard 
                        key={recipe.id} 
                        recipe={recipe} 
                        onClick={() => handleRecipeClick(recipe)}
                        isFavorite={favoriteIds.has(recipe.id)}
                        onToggleFavorite={handleToggleFavorite}
                      />
                    ))}
                  </div>
                  
                  {/* Show more button if more than 6 recipes */}
                  {categoryRecipes.length > 6 && (
                    <button 
                      onClick={() => setFilters(f => ({ ...f, categoria: categoria.id }))}
                      className="w-full py-2 text-[#D4AF37] text-sm font-medium hover:bg-[#D4AF37]/10 rounded-lg transition-colors"
                    >
                      Ver todas ({categoryRecipes.length}) →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Recipe Detail Modal */}
      {selectedRecipe && (
        <RecipeDetail 
          recipe={selectedRecipe} 
          onClose={() => setSelectedRecipe(null)} 
        />
      )}
    </div>
    </ModuleGate>
  );
};

export default RecetasPage;
